import { isBookkeepingHeroAllowed } from '@components'

import { AttachmentFile } from '@billy/design-system'
import { trackError } from '@billy/track-error'

import { Contact } from '@features/contacts/types/contact'
import { fetchCompanies } from '@modules-deprecated/app/companies'
import { Company } from '@modules-deprecated/app/companies/types'
import { getCompanyName } from '@modules-deprecated/app/companies/utils/getCompanyName'
import { findContactsWithBohr } from '@modules-deprecated/app/contacts'
import { ChartOfAccountId } from '@modules-deprecated/app/organization/enums/chartOfAccountId'
import { TaxRate } from '@modules-deprecated/app/taxrates/types'
import { buildBillLines } from '@views/bills/utils/buildBillLines'

import { AttachmentParsedDataVendor } from '../../../../../types/attachmentParsedDataVendor'
import { getApiCountryKey } from '../../../../../utils/getApiCountryKey'
import { getRegion } from '../../../../../utils/getRegion'
import { BillForm, BillFormLine, BillFormPartial } from './formData'
import { getBookkeepingTagsFromTagScores } from './getBookkeepingTagsFromTagScores'
import { getCombinedVendorProp } from './getCombinedVendorProp'
import { getParsedDataFromAttachment } from './getParsedDataFromAttachment'
import { isBillLineReplaceableByAttachmentData } from './isBillLineReplaceableByAttachmentData'

const TAG_SUGGESTIONS_AMOUNT = 3

interface GetBillFormFromAttachmentDataProps {
  attachmentFile?: AttachmentFile
  billForm: BillForm
  chartOfAccountId?: ChartOfAccountId
  countryId?: string
  locale: string
  organizationId: string
  taxRates?: TaxRate[]
  withBookkeepingSuggestions?: boolean
}

export const getBillFormFromAttachmentData = async ({
  attachmentFile,
  billForm,
  chartOfAccountId,
  countryId,
  locale,
  organizationId,
  taxRates,
  withBookkeepingSuggestions = true,
}: GetBillFormFromAttachmentDataProps): Promise<BillFormPartial> => {
  const attachmentFileParsedData = getParsedDataFromAttachment(attachmentFile)

  if (!attachmentFileParsedData) {
    return {}
  }

  const buildVendorFromLookup = (
    lookupVendor: Company | undefined,
    supplier: AttachmentParsedDataVendor,
    supplierAddress: AttachmentParsedDataVendor['address'],
  ): Partial<Contact> => ({
    ...getCombinedVendorProp({
      propName: 'registrationNo',
      lookupVendor,
      vendorKey: 'id',
      supplier,
      supplierKey: 'registrationNo',
    }),
    ...getCombinedVendorProp({
      propName: 'vatNo',
      lookupVendor,
      vendorKey: 'vatNo',
      supplier,
      supplierKey: 'vatNo',
    }),
    ...getCombinedVendorProp<AttachmentParsedDataVendor['address']>({
      propName: 'countryId',
      lookupVendor: undefined,
      vendorKey: undefined,
      supplier: supplierAddress,
      supplierKey: 'country',
    }), // No lookupVendor key for country
    ...((getCompanyName(lookupVendor) ?? supplier.name ?? undefined)
      ? { name: getCompanyName(lookupVendor) ?? supplier.name ?? undefined }
      : {}),
    ...getCombinedVendorProp<AttachmentParsedDataVendor['address']>({
      propName: 'cityText',
      lookupVendor,
      vendorKey: 'city',
      supplier: supplierAddress,
      supplierKey: 'city',
    }),
    ...getCombinedVendorProp<AttachmentParsedDataVendor['address']>({
      propName: 'street',
      lookupVendor: undefined,
      vendorKey: undefined,
      supplier: supplierAddress,
      supplierKey: 'street',
    }), // No lookupVendor key for street
    ...getCombinedVendorProp({ propName: 'phone', lookupVendor, vendorKey: 'phone', supplier, supplierKey: 'phone' }),
    ...getCombinedVendorProp<AttachmentParsedDataVendor['address']>({
      propName: 'zipcodeText',
      lookupVendor,
      vendorKey: 'zipcode',
      supplier: supplierAddress,
      supplierKey: 'zip',
    }),
  })

  const getVendorData = async (): Promise<{ vendor?: Partial<Contact>; vendorSuggestions?: Partial<Contact>[] }> => {
    const supplier = attachmentFileParsedData.vendor
    const {
      name: supplierName,
      address: supplierAddress,
      registrationNo: supplierRegistrationNo,
      vatNo: supplierVatNo,
    } = supplier

    const apiCountryKey = getApiCountryKey(countryId)
    const registrationNo = supplierRegistrationNo || supplierVatNo

    /* Look by registration number only */
    let lookupCompanies = { companies: [] as Company[] }
    if (registrationNo) {
      try {
        lookupCompanies = await fetchCompanies(registrationNo, apiCountryKey)
      } catch (error) {
        trackError(error)
      }
    }

    /* Registration number lookup found nothing: fall back to a name-only search, nothing else */
    if (!lookupCompanies.companies.length && supplierName) {
      try {
        lookupCompanies = await fetchCompanies(supplierName, apiCountryKey)
      } catch (error) {
        trackError(error)
      }
    }

    const lookupVendor = lookupCompanies?.companies?.[0]

    const contactSearchName = getCompanyName(lookupVendor) || supplierName

    if (contactSearchName) {
      try {
        const vendors = await findContactsWithBohr(organizationId, contactSearchName, registrationNo)

        if (vendors.length) {
          return { vendor: vendors[0] }
        }
      } catch (error) {
        trackError(error)
        return {}
      }
    }

    /* Several companies matched the lookup: let the user pick instead of guessing */
    if (lookupCompanies.companies.length > 1) {
      return {
        vendorSuggestions: lookupCompanies.companies.map((company) =>
          buildVendorFromLookup(company, supplier, supplierAddress),
        ),
      }
    }

    return { vendor: buildVendorFromLookup(lookupVendor, supplier, supplierAddress) }
  }

  const { tagScores, dates, currency, amounts, vatSummary } = attachmentFileParsedData
  const { vendor, vendorSuggestions } = await getVendorData()

  const tagScoresLimited = tagScores.length ? tagScores.slice(0, TAG_SUGGESTIONS_AMOUNT) : undefined
  const isBookkeepingTagSuggestionsEnabled =
    withBookkeepingSuggestions && !!chartOfAccountId && isBookkeepingHeroAllowed(chartOfAccountId)

  const bookkeepingTagSuggestions =
    isBookkeepingTagSuggestionsEnabled && tagScoresLimited && tagScoresLimited.length
      ? await getBookkeepingTagsFromTagScores(tagScoresLimited, locale, organizationId)
      : undefined

  /* Bohr can find VAT value or it can find nothing, sending null/undefined */
  /* We're only interested in absolute values - 0 or 0.25, or 0.06, etc */
  const hasVATdetected = typeof amounts.totalVat === 'number' && amounts.totalVat !== 0

  /* For now, we're only autocompleting first bill line */
  const existingBillLine = billForm?.billLines?.[0]
  const existingTag = existingBillLine?.bookkeepingTag?.response
  const autoCompletedFields = billForm.autoCompletedFields || {}
  const isEmptyBillLine = isBillLineReplaceableByAttachmentData(billForm)

  let billLines: Partial<BillFormLine>[] = []

  const region = getRegion({
    vendorCountryCode: vendor?.countryId,
    organizationCountryCode: countryId,
  })

  if (billForm.billLines && !isEmptyBillLine) {
    // If we already have bill lines, we don't want to override them, we only want to add bookkeepingTag if we don't have it yet
    billLines = billForm.billLines
  } else {
    // If we don't have bill lines yet, we want to build them based on attachment data
    billLines = buildBillLines({ vatSummary, existingTag, region, taxRates, hasVATdetected, amounts })
  }

  return {
    ...(autoCompletedFields ? { autoCompletedFields } : {}),
    ...(dates.issued ? { billDate: dates.issued } : {}),
    ...(bookkeepingTagSuggestions ? { bookkeepingTagSuggestions } : {}),
    ...(currency ? { currencyId: currency } : {}),
    ...(dates.due ? { dueDate: dates.due } : {}),
    ...(vendor ? { vendor } : {}),
    ...(vendorSuggestions?.length ? { vendorSuggestions } : {}),
    ...(billLines.length ? { billLines } : {}),
  }
}
